import useFeedback from 'common/presentation/providers/feedback.provider';
import { CHomeRepositoryImpl } from 'features/home/data/repositories/home-repository-impl';
import { Partner } from 'features/home/domain/entities/partner';
import { Product } from 'features/home/domain/entities/product';
import { Question } from 'features/home/domain/entities/question';
import { IRecommendReq } from 'features/home/domain/entities/recommend.model';
import { GetPartners } from 'features/home/domain/usecases/get-partners';
import { GetProducts } from 'features/home/domain/usecases/get-products';
import { GetQuestions } from 'features/home/domain/usecases/get-questions';
import { SendRecommendation } from 'features/home/domain/usecases/send-recommendation';
import React, {
	createContext, MutableRefObject, useContext, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { validateEmail, validatePhone } from 'utils/validation';

type TContextProps = {
	loading: boolean;
	error: string;
	products: Product[];
	getProducts: () => Promise<void>;
	partnerLoading: boolean;
	partnerError: string;
	partners: Partner[];
	getPartners: () => Promise<void>;
	questionLoading: boolean;
	questionError: string;
	questions: Question[];
	getQuestions: (lang: string) => Promise<void>

	// Recommend
	recommendValues: IRecommendReq
	recommendErrors: IRecommendReq
	redrawCaptcha: MutableRefObject<() => void>
	captcha: string
	recommendLoading: boolean
	setRecommendValues: (params: IRecommendReq) => void
	setRecommendErrors: (params: IRecommendReq) => void
	setCaptcha: (value: string) => void
	submitRecommendReq: () => Promise<boolean>
}

const HomeContext = createContext({} as TContextProps);

export const HomeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
	const [products, setProducts] = useState([] as Product[]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');

	const [partners, setPartners] = useState([] as Partner[]);
	const [partnerLoading, setPartnerLoading] = useState(false);
	const [partnerError, setPartnerError] = useState('');

	const [questions, setQuestions] = useState([] as Question[]);
	const [questionLoading, setQuestionLoading] = useState(false);
	const [questionError, setQuestionError] = useState('');

	const [recommendValues, setRecommendValues] = useState({} as IRecommendReq);
	const [recommendErrors, setRecommendErrors] = useState({} as IRecommendReq);
	const redrawCaptcha = useRef() as MutableRefObject<() => void>;
	const [captcha, setCaptcha] = React.useState('');
	const [recommendLoading] = useState(false);

	const { t } = useTranslation('translations');
	const { addDialog } = useFeedback();

	const homeRepository = new CHomeRepositoryImpl();

	const getProducts = async () => {
		setLoading(true);
		const productUseCase = new GetProducts(homeRepository);
		const productResult = await productUseCase.call();
		if (productResult.isRight()) {
			setProducts(productResult.value);
		} else {
			setError(productResult.error);
		}
		setLoading(false);
	};

	const getPartners = async () => {
		setPartnerLoading(true);
		const partnerUseCase = new GetPartners(homeRepository);
		const partnerResult = await partnerUseCase.call();
		if (partnerResult.isRight()) {
			setPartners(partnerResult.value);
		} else {
			setPartnerError(partnerResult.error);
		}
		setPartnerLoading(false);
	};

	const getQuestions = async (lang: string) => {
		setQuestionLoading(true);
		const questionUseCase = new GetQuestions(homeRepository);
		const questionsResult = await questionUseCase.call(lang);
		if (questionsResult.isRight()) {
			setQuestions(questionsResult.value);
		} else {
			setQuestionError(questionsResult.error);
		}
		setQuestionLoading(false);
	};

	const submitRecommendReq = async (): Promise<boolean> => {
		const errors = {
			name: '',
			supplierName: '',
			supplierEmail: '',
			supplierPhone: '',
			captcha: '',
		};
		if (captcha === '' || recommendValues.captcha !== captcha) {
			errors.captcha = t('captcha_required');
			redrawCaptcha.current();
		}
		if (!recommendValues.name) {
			errors.name = t('please_enter_name');
		}
		if (!recommendValues.supplierName) {
			errors.supplierName = t('please_enter_surname');
		}
		if (!recommendValues.supplierEmail && !recommendValues.supplierPhone) {
			errors.supplierEmail = t('please_enter_email_or_phone');
			errors.supplierPhone = t('please_enter_email_or_phone');
		} else if (recommendValues.supplierEmail && !validateEmail(recommendValues.supplierEmail)) {
			errors.supplierEmail = t('invalid_email_format');
		} else if (recommendValues.supplierPhone && !validatePhone(recommendValues.supplierPhone)) {
			errors.supplierPhone = t('invalid_phone_format');
		}
		setRecommendErrors(errors);
		if (
			errors.captcha
			|| errors.name
			|| errors.supplierEmail
			|| errors.supplierName
			|| errors.supplierPhone
		) {
			return false;
		}
		setRecommendErrors({} as IRecommendReq);
		const sendRecommendUseCase = new SendRecommendation(homeRepository);
		const resp = await sendRecommendUseCase.call(recommendValues);
		if (resp.isRight()) {
			setRecommendErrors({} as IRecommendReq);
			setRecommendValues({} as IRecommendReq);
			addDialog({
				title: t('success'),
				message: t('success_info'),
				error: false,
			});
			return true;
		}
		addDialog({
			title: t('error'),
			message: resp.error,
			error: true,
		});
		return false;
	};

	const value = useMemo(() => ({
		products,
		loading,
		error,
		getProducts,
		partnerLoading,
		partners,
		partnerError,
		getPartners,
		questionLoading,
		questionError,
		questions,
		getQuestions,
		recommendValues,
		recommendErrors,
		redrawCaptcha,
		captcha,
		recommendLoading,
		setRecommendValues,
		setRecommendErrors,
		setCaptcha,
		submitRecommendReq,
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}), [
		loading,
		error,
		products,
		partnerError,
		partnerLoading,
		partners,
		questions,
		questionError,
		questionLoading,
		recommendValues,
		recommendErrors,
		redrawCaptcha,
		captcha,
		recommendLoading,
		setRecommendValues,
		setRecommendErrors,
		setCaptcha,
	]);

	return <HomeContext.Provider value={value}>{children}</HomeContext.Provider>;
};

export default function useHome() {
	return useContext(HomeContext);
}
